import { InspectionReportDownload } from '@components/Shared/Inspection/InspectionReportDownload';
import { useLocalization } from '@lib/localization';
import { type Inspection } from '@lib/spproject/types';
import {
  AccordionDetails,
  AccordionSummary,
  Box, List,
  Pagination,
  Tooltip,
  Typography
} from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import { Calendar } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { AngleDownIcon } from 'react-line-awesome';
import { Accordion, EmptyPOIContainer, InfoChip, InspectionHeader, InspectionInfo } from './Inspections.styled';
import InspectionPointItem from './InspectionPointItem';

interface Props {
  inspection: IInspection;
  isExpanded: boolean;
  onToggleExpand: (inspectionId: string) => void;
  onPoiClick: (inspectionId: string, poi: Inspection.PointOfInterest) => void;
  projectId: string;
  projectName: string;
}

const POIS_PER_PAGE = 5;

const InspectionAccordion = memo<Props>(({ inspection, isExpanded, onToggleExpand, onPoiClick, projectId, projectName }) => {
  const { t } = useLocalization();
  const [currentPage, setCurrentPage] = useState(1);

  const pois = useMemo<Inspection.PointOfInterest[]>(() => {
    return inspection.pointOfInterests ?? [];
  }, [inspection.pointOfInterests]);

  const paginatedPOIs = useMemo(() => {
    const start = (currentPage - 1) * POIS_PER_PAGE;
    return pois.slice(start, start + POIS_PER_PAGE);
  }, [pois, currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(pois.length / POIS_PER_PAGE);
  }, [pois.length]);

  const handleToggle = useCallback(() => {
    onToggleExpand(inspection.info.id);
  }, [onToggleExpand, inspection.info.id]);

  const handlePageChange = useCallback((_: unknown, value: number) => {
    setCurrentPage(prev => (prev === value ? prev : value));
  }, []);

  useEffect(() => {
    if (!isExpanded) setCurrentPage(1);
  }, [isExpanded]);

  return (
    <Accordion expanded={isExpanded} onChange={handleToggle}>
      <Box sx={{ display: 'flex' }}>
        <AccordionSummary expandIcon={<AngleDownIcon />} sx={{ flexGrow: 1 }}>
          <InspectionHeader>
            <InspectionInfo>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {inspection.info.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {inspection.info.id}
              </Typography>
            </InspectionInfo>
            <Tooltip title={t('Common_DateCreated')}>
              <InfoChip
                icon={<Calendar size={14} style={{ marginRight: '1px' }} />}
                label={new Date(inspection.info.dateCreated).toAgoString()}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          </InspectionHeader>
        </AccordionSummary>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InspectionReportDownload inspection={inspection} projectId={projectId} projectName={projectName} />
        </Box>
      </Box>

      <AccordionDetails>
        <List sx={{ padding: 0 }}>
          {paginatedPOIs.map((poi, i) => (
            <InspectionPointItem
              key={poi.id}
              poi={poi}
              inspectionId={inspection.info.id}
              onPoiClick={onPoiClick}
              isFirst={i === 0}
            />
          ))}

          {inspection.pointOfInterests?.length === 0 && (
            <EmptyPOIContainer>
              <Typography color="text.secondary">{t('POI_NotFound')}</Typography>
            </EmptyPOIContainer>
          )}
        </List>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
});

export default InspectionAccordion;
